/*
 * SonarQube
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * mailto:info AT sonarsource DOT com
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU Lesser General Public
 * License as published by the Free Software Foundation; either
 * version 3 of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this program; if not, write to the Free Software Foundation,
 * Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 */

import { sortBy } from 'lodash';
import * as React from 'react';
import { MeasureEnhanced } from '~shared/types/measures';
import { MetricKey } from '~shared/types/metrics';
import { CardSeparator, Link } from '../../design-system';
import { translate } from '../../helpers/l10n';
import { CAYC_CONDITION_ORDER_PRIORITIES } from '../../helpers/quality-gates';
import { BranchLike } from '../../types/branch-like';
import { QualityGateStatusConditionEnhanced } from '../../types/quality-gates';
import { Component } from '../../types/types';
import IssuesLinkCausedByUpgrade from './IssuesLinkCausedByUpgrade';
import QualityGateCondition from './QualityGateCondition';
import QualityGateSimplifiedCondition from './QualityGateSimplifiedCondition';

const LEVEL_ORDER = ['ERROR', 'WARN'];

export interface QualityGateConditionsProps {
  branchLike?: BranchLike;
  collapsible?: boolean;
  component: Pick<Component, 'key'>;
  failedConditions: QualityGateStatusConditionEnhanced[];
  isBuiltInQualityGate?: boolean;
  measures?: MeasureEnhanced[];
}

const MAX_CONDITIONS = 5;

export function QualityGateConditions(props: Readonly<QualityGateConditionsProps>) {
  const { branchLike, collapsible, component, failedConditions, isBuiltInQualityGate, measures } =
    props;
  const [collapsed, toggleCollapsed] = React.useState(Boolean(collapsible));

  const handleToggleCollapsed = React.useCallback(() => {
    toggleCollapsed(!collapsed);
  }, [collapsed]);

  const isSimplifiedCondition = React.useCallback(
    (condition: QualityGateStatusConditionEnhanced) => {
      const { metric } = condition.measure;
      return metric.key === MetricKey.new_violations && isBuiltInQualityGate;
    },
    [isBuiltInQualityGate],
  );

  const sortedConditions = sortBy(failedConditions, [
    (condition) => CAYC_CONDITION_ORDER_PRIORITIES[condition.metric],
    (condition) => LEVEL_ORDER.indexOf(condition.level),
  ]);

  const fromSonarQubeUpdateIssuesMeasure = measures?.find(
    (measure) => measure.metric.key === MetricKey.from_sonarqube_update_issues,
  );

  let renderConditions;
  let renderCollapsed;

  if (collapsed && sortedConditions.length > MAX_CONDITIONS) {
    renderConditions = sortedConditions.slice(0, MAX_CONDITIONS);
    renderCollapsed = true;
  } else {
    renderConditions = sortedConditions;
    renderCollapsed = false;
  }

  return (
    <ul id="overview-quality-gate-conditions-list">
      {renderConditions.map((condition, idx) => (
        <div key={condition.measure.metric.key}>
          {isSimplifiedCondition(condition) ? (
            <QualityGateSimplifiedCondition
              branchLike={branchLike}
              component={component}
              condition={condition}
            />
          ) : (
            <QualityGateCondition
              branchLike={branchLike}
              component={component}
              condition={condition}
            />
          )}
          <IssuesLinkCausedByUpgrade
            branchLike={branchLike}
            className="sw-ml-16 sw-mt-1"
            component={component}
            condition={condition}
            fromSonarQubeUpdateIssuesMeasure={fromSonarQubeUpdateIssuesMeasure}
            isNewCodePeriod={condition.period != null}
            metric={condition.measure.metric}
          />
          {idx !== renderConditions.length - 1 && <CardSeparator />}
        </div>
      ))}
      {renderCollapsed && (
        <li className="sw-flex sw-justify-center sw-my-3">
          <Link onClick={handleToggleCollapsed} preventDefault to={{}}>
            <span className="sw-font-semibold sw-text-sm">{translate('show_more')}</span>
          </Link>
        </li>
      )}
    </ul>
  );
}

export default React.memo(QualityGateConditions);
